import { useInView } from "@/hooks/useInView";
import { Button } from "@/components/ui/button";
import { Shield, Sparkles, Heart } from "lucide-react";

const CHILD_DASHBOARD_URL = `${process.env.PUBLIC_URL}/hero-app.webp`;
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.neuronest.send&pcampaignid=web_share";

const TRUST_ITEMS = [
  { icon: Sparkles, text: "Designed for neurodivergent individuals" },
  { icon: Shield, text: "Built with sensory-safe design principles" },
  { icon: Heart, text: "No clinical language, no judgment, no pressure" },
];

export default function HeroSection() {
  const [ref, inView] = useInView();

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[90vh] flex flex-col items-center justify-center overflow-hidden pt-20 pb-16 md:pt-24 md:pb-24"
    >
      {/* Decorative blobs */}
      <div className="absolute top-16 -left-40 w-[500px] h-[500px] bg-brand-sage rounded-full opacity-[0.08] blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 -right-40 w-[400px] h-[400px] bg-brand-purple rounded-full opacity-[0.08] blur-[100px] pointer-events-none" />
      <div className="absolute top-1/3 right-1/4 w-[300px] h-[300px] bg-brand-peach rounded-full opacity-[0.06] blur-[80px] pointer-events-none" />

      <div
        ref={ref}
        className={`relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-20 w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center transition-all duration-700 ${
          inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        {/* Text */}
        <div className="text-center lg:text-left">
          <h1
            data-testid="hero-headline"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2F3A56] leading-[1.15] tracking-tight mb-6"
          >
            The world doesn't need to feel{" "}
            <span className="text-brand-teal">overwhelming.</span>
          </h1>
          <p
            data-testid="hero-subheadline"
            className="text-base md:text-lg text-[#5A6376] leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0"
          >
            NeuroNest is a sensory-safe companion app that helps you navigate
            daily life with greater ease, predictability, and self-compassion.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-center lg:justify-start mb-4">
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-primary"
            >
              <img
                src={`${process.env.PUBLIC_URL}/google-play-badge.png`}
                alt="Get it on Google Play"
                className="h-14"
              />
            </a>
            <Button
              data-testid="hero-cta-secondary"
              variant="outline"
              className="rounded-full border-2 border-brand-purple text-brand-purple hover:bg-brand-purple hover:text-white px-8 py-3 text-base font-semibold transition-all duration-300 h-auto"
              asChild
            >
              <a href="#features">See What's Inside</a>
            </Button>
          </div>
          <p className="text-sm text-[#8B95A5] font-medium">
            No scores. No streaks. No pressure. Just tools designed with your
            nervous system in mind.
          </p>
        </div>

        {/* App Preview - Phone Mockup */}
        <div className="flex justify-center lg:justify-end">
          <div className="relative">
            {/* Phone frame */}
            <div className="relative w-[260px] md:w-[290px]">
              <div className="relative bg-[#2F3A56] rounded-[36px] p-[9px] shadow-[0_20px_60px_rgba(47,58,86,0.2)]">
                {/* Notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[90px] h-[20px] bg-[#2F3A56] rounded-b-xl z-10" />
                {/* Screen */}
                <div className="relative rounded-[28px] overflow-hidden bg-[#F0F1F3] min-h-[400px] md:min-h-[450px]">
                  <img
                    data-testid="hero-app-preview"
                    src={CHILD_DASHBOARD_URL}
                    alt="NeuroNest app — home dashboard with quick access to all tools"
                    className="w-full h-auto block"
                    loading="eager"
                    fetchpriority="high"
                  />
                </div>
                {/* Home indicator */}
                <div className="absolute bottom-[5px] left-1/2 -translate-x-1/2 w-[80px] h-[3px] bg-white/30 rounded-full" />
              </div>
            </div>
            {/* Floating accent card */}
            <div className="absolute -bottom-4 -left-8 md:-left-12 bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.1)] px-4 py-3 flex items-center gap-3 border border-[#E7E9EC]">
              <div className="w-9 h-9 rounded-xl bg-brand-teal/10 flex items-center justify-center">
                <Heart size={16} className="text-brand-teal fill-brand-teal" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-xs font-semibold text-[#2F3A56]">No pressure</p>
                <p className="text-[10px] text-[#8B95A5]">Your pace, always</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust bar */}
      <div
        data-testid="trust-bar"
        className={`relative z-10 mt-16 md:mt-20 max-w-4xl mx-auto px-6 w-full transition-all duration-700 delay-300 ${
          inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        <blockquote className="text-center text-[#5A6376] italic text-base md:text-lg mb-8 leading-relaxed">
          "Let's create a space that feels safe, predictable, and supportive."
        </blockquote>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
          {TRUST_ITEMS.map((item) => (
            <div
              key={item.text}
              className="flex items-center gap-3 text-sm text-[#5A6376]"
            >
              <div className="w-8 h-8 rounded-full bg-brand-teal/10 flex items-center justify-center flex-shrink-0">
                <item.icon size={16} className="text-brand-teal" strokeWidth={1.5} />
              </div>
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
